import { useState } from 'react'

import type { VerifiedLearningEntry } from '../../../app/ai-os'
import {
  didSomething,
  mintOperationKey,
  type AiOsOpOutcome,
  type OperationKey,
} from '../../../app/ai-os-operation'
import { canDisableLearning } from '../../../app/module-evolution'
import { Button } from '../../../components/button'
import { t } from '../../../copy'
import { T } from '../../../copy/text'

/**
 * 「别再用这条经验」。
 *
 * # 它不是回滚
 *
 * 只改这一行的状态——「以后别照这条办」，**不是「把照它办过的事撤回来」**。
 * 两件事的下一步完全不同，而它们在界面上很容易被读成同一件。
 *
 * # `reason` 必填，而且是这个按钮存在的一半理由
 *
 * 契约原话：一条「被关掉但说不出为什么」的经验，下一个看到它的人只会把它再打开。
 * 所以这里不是「点一下就关」，是**先要一句话再关**。
 *
 * # 键的寿命与 retry 完全一样
 *
 * 两个口走同一张台账（`op` 不同而已），所以那套规矩一字不差地照搬：
 * 失败过的键换掉，没拿到响应的留着。判断在 `app/ai-os-operation.ts`。
 */

export interface LearningDisableProps {
  entry: VerifiedLearningEntry
  onDisable: (vars: { idempotencyKey: string; expectedVersion: number; reason: string }) => void
  pending: boolean
  outcome?: AiOsOpOutcome
  serverMessage?: string
}

export function LearningDisable({
  entry,
  onDisable,
  pending,
  outcome,
  serverMessage,
}: LearningDisableProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [key, setKey] = useState<OperationKey>(() => mintOperationKey())
  const [seen, setSeen] = useState<AiOsOpOutcome | undefined>(undefined)

  if (outcome && outcome !== seen) {
    setSeen(outcome)
    setKey((k) => k.next(outcome))
    // 真的关掉了才收起来。重放与各种 409 都留着面板，
    // 因为**那几档用户还有下一步要做**
    if (outcome.kind === 'ok' && didSomething(outcome)) setOpen(false)
  }

  // 已经回退掉的关不了（契约 409 `not_disableable`），已经关掉的再关一次没有意义。
  // **不给一个点了必然失败的按钮**
  if (!canDisableLearning(entry)) return null

  return (
    <div data-testid="ai-os-disable" className="mt-npc-gap-xs">
      {!open ? (
        <Button size="sm" variant="ghost" data-testid="ai-os-disable-open" onClick={() => setOpen(true)}>
          <T id="ai_os.action.disable_learning" />
        </Button>
      ) : (
        <div className="flex flex-col gap-npc-gap-xs">
          <label className="text-npc-meta text-npc-ink-softer" htmlFor={`disable-${entry.id}`}>
            <T id="ai_os.field.problem_evidence" />
          </label>
          <textarea
            id={`disable-${entry.id}`}
            data-testid="ai-os-disable-reason"
            value={reason}
            rows={2}
            maxLength={500}
            placeholder={t('ai_os.field.problem_evidence.hint')}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-gap-sm py-npc-gap-xs text-npc-caption text-npc-ink"
          />
          <div className="flex flex-wrap items-center gap-npc-gap-sm">
            <Button
              size="sm"
              data-testid="ai-os-disable-submit"
              // 说不出为什么就不许关。**这道门在界面这一侧，
              // 不是等服务端 400 回来才说**
              disabled={pending || reason.trim() === '' || entry.version === undefined}
              onClick={() => {
                if (entry.version === undefined) return
                onDisable({
                  idempotencyKey: key.key,
                  expectedVersion: entry.version,
                  reason: reason.trim(),
                })
              }}
            >
              <T id="ai_os.action.disable_learning" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="ai-os-disable-cancel"
              onClick={() => setOpen(false)}
            >
              <T id="ai_os.action.cancel" />
            </Button>
            {pending ? (
              <span data-testid="ai-os-disable-pending" className="text-npc-caption text-npc-ink-soft">
                <T id="ai_os.loading.submitting" />
              </span>
            ) : null}
          </div>
        </div>
      )}

      {outcome && outcome.kind === 'ok' && !didSomething(outcome) ? (
        <p data-testid="ai-os-disable-replayed" className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          {/* 重放：**什么都没发生**，回的是当初那份结果。
              与「关好了」在 HTTP 上都是 200，不分开的话用户会以为这次也关了一遍 */}
          <T id="ai_os.conflict.already_submitted" />
        </p>
      ) : null}

      {outcome && outcome.kind !== 'ok' ? (
        <p
          data-testid={
            outcome.kind === 'in_flight'
              ? 'ai-os-disable-in-flight'
              : outcome.kind === 'already_failed'
                ? 'ai-os-disable-already-failed'
                : outcome.kind === 'not_disableable'
                  ? 'ai-os-disable-not-disableable'
                  : 'ai-os-disable-error'
          }
          className="m-0 mt-npc-gap-xs rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-gap-sm py-npc-gap-xs text-npc-caption text-npc-amber-ink"
        >
          {serverMessage ? (
            serverMessage
          ) : outcome.kind === 'version_conflict' ? (
            <T id="ai_os.conflict.changed_elsewhere" />
          ) : outcome.kind === 'idempotency_mismatch' ? (
            <T id="ai_os.conflict.same_key_different_content" />
          ) : (
            <T id="ai_os.error.create_failed" />
          )}
        </p>
      ) : null}
    </div>
  )
}
